const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5050/api'

interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: string
  message?: string
  code?: string
}

export type CustomerType = 'INDIVIDUAL' | 'COMPANY'

export interface RegisterPayload {
  email: string
  password: string
  customer_type: CustomerType
  first_name?: string
  last_name?: string
  company_name?: string
}

export interface RegisterResult {
  customer_id: number
  email: string
  customer_type: CustomerType
  /** true = ต้องยืนยันอีเมลก่อนล็อกอิน (ส่งลิงก์ไปที่อีเมลแล้ว) / false = ล็อกอินได้ทันที */
  requires_verification: boolean
}

/** ล็อกอินไม่ได้เพราะยังไม่ยืนยันอีเมล — หน้า Login ใช้แสดงปุ่ม "ส่งอีเมลยืนยันอีกครั้ง" */
export class EmailNotVerifiedError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'EmailNotVerifiedError'
  }
}

export interface LoginResult {
  access_token: string
  refresh_token: string
}

async function parseJson<T>(res: Response): Promise<ApiResponse<T>> {
  try {
    return await res.json()
  } catch {
    return { success: false, error: 'เชื่อมต่อเซิร์ฟเวอร์ไม่สำเร็จ' }
  }
}

export const register = async (payload: RegisterPayload): Promise<RegisterResult> => {
  const res = await fetch(`${API_URL}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  const json = await parseJson<RegisterResult>(res)
  if (!json.success || !json.data) throw new Error(json.error || json.message || 'สมัครสมาชิกไม่สำเร็จ')
  return json.data
}

interface LoginResponse extends ApiResponse<undefined> {
  access_token?: string
  refresh_token?: string
}

export const login = async (email: string, password: string): Promise<LoginResult> => {
  const res = await fetch(`${API_URL}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  })
  const json: LoginResponse = await parseJson(res)
  if (res.status === 429) throw new Error('เข้าสู่ระบบบ่อยเกินไป กรุณาลองใหม่ใน 1 นาที')
  if (json.code === 'EMAIL_NOT_VERIFIED') {
    throw new EmailNotVerifiedError(json.error || 'กรุณายืนยันอีเมลก่อนเข้าสู่ระบบ')
  }
  if (!json.success || !json.access_token || !json.refresh_token) {
    throw new Error(json.error || json.message || 'อีเมลหรือรหัสผ่านไม่ถูกต้อง')
  }
  return { access_token: json.access_token, refresh_token: json.refresh_token }
}

export const logout = async (refreshToken: string): Promise<void> => {
  await fetch(`${API_URL}/auth/logout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  })
}

export const refreshAccessToken = async (refreshToken: string): Promise<LoginResult> => {
  const res = await fetch(`${API_URL}/auth/refresh-token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  })
  const json: LoginResponse = await parseJson(res)
  if (!json.success || !json.access_token || !json.refresh_token) {
    throw new Error(json.error || json.message || 'Token หมดอายุ กรุณาเข้าสู่ระบบใหม่')
  }
  return { access_token: json.access_token, refresh_token: json.refresh_token }
}

export const verifyEmail = async (token: string): Promise<{ already_verified: boolean }> => {
  const res = await fetch(`${API_URL}/auth/verify-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  })
  const json = await parseJson<{ verified: boolean; already_verified: boolean }>(res)
  if (!json.success || !json.data) throw new Error(json.error || json.message || 'ยืนยันอีเมลไม่สำเร็จ')
  return { already_verified: json.data.already_verified }
}

/** ขอส่งลิงก์ยืนยันใหม่ — ตอบข้อความเดียวกันเสมอไม่ว่าอีเมลนั้นมีบัญชีหรือไม่ */
export const resendVerification = async (email: string): Promise<string> => {
  const res = await fetch(`${API_URL}/auth/resend-verification`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  })
  if (res.status === 429) throw new Error('ขอส่งอีเมลบ่อยเกินไป กรุณาลองใหม่ภายหลัง')
  const json = await parseJson<{ message: string }>(res)
  if (!json.success) throw new Error(json.error || json.message || 'ส่งอีเมลไม่สำเร็จ')
  return json.data?.message || 'ส่งอีเมลยืนยันให้แล้ว'
}
